import React, { useState } from "react";
import { useApolloClient, useMutation, useQuery } from "@apollo/client/react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CircularProgress from "@mui/material/CircularProgress";
import { UPDATE_ASSET_MUTATION } from "../../../constants/graphql/mutations";
import { ASSET_CHARGES_INFO_QUERY, PROJECT_BILLING_RATES_QUERY, ASSET_REPLAY_LOG_QUERY } from "../../../constants/graphql/queries";
import { getBillingQuantityOptions } from "../../../utils/billingHelpers";
import CopyReplayLogModal from "../../../views/asset/CopyReplayLogModal";
import InventoryTransactionMultipleModal from "../../modals/InventoryTransactionMultipleModal";
import DimensionsModal from "../../TablePro/fields/DimensionsField";
import labels from "../../../constants/displayFields";
import { getPalletRateKey, BILLING_TYPE_FORMULAS } from "./billingTypeConfig";
import {
  HUNDREDWEIGHT,
  BY_THE_PALLET,
  BY_THE_PALLET_WAREHOUSE_BUILT,
  SQUARE_FOOT_ITEM,
  SQUARE_FOOT_LOCATION,
  CUBIC_FOOT_ITEM,
  CUBIC_FOOT_LOCATION,
  STORAGE_LOCATIONS_OCCUPIED,
  BY_THE_UNIT_ITEM,
  BY_THE_VAULT,
} from "../../project/settings/utils/billingTypes";
import { formatRackLocationLabel } from "src/utils/rackLocations";

export default function Step2EditView({ asset, isHistoricalData, onBack, assetReplayLogId, onUpdateReplayLogId }) {
  const apolloClient = useApolloClient();
  const [billingType, setBillingType] = useState(asset.billingType || "");
  const [palletType, setPalletType] = useState(asset.palletType || "");
  const [unitType, setUnitType] = useState(asset.unitType || "");
  const [squareFootage, setSquareFootage] = useState(asset.squareFootage || "");
  const [cubicFootage, setCubicFootage] = useState(asset.cubicFootage || "");
  const [weight, setWeight] = useState(asset.weight || "");
  const [dimensions, setDimensions] = useState(asset.dimensions || []);
  
  const [showReplayLogModal, setShowReplayLogModal] = useState(false);
  const [showInventoryModal, setShowInventoryModal] = useState(false);
  const [showDimensionsModal, setShowDimensionsModal] = useState(false);

  // Fetch project billing rates
  const { data: projectData } = useQuery(PROJECT_BILLING_RATES_QUERY, {
    variables: { projectId: asset.project?.id },
    skip: !asset.project?.id,
  });

  const [updateAsset, { loading: updateLoading }] = useMutation(UPDATE_ASSET_MUTATION, {
    refetchQueries: [
      {
        query: ASSET_CHARGES_INFO_QUERY,
        variables: { assetId: asset.id },
      },
    ],
    onCompleted: (data) => {
      if (data.updateAsset.errors && data.updateAsset.errors.length > 0) {
        alert(`Error: ${data.updateAsset.errors[0].message}`);
      } else {
        // Return to Step 1 to show updated charges
        onBack();
      }
    },
    onError: (error) => {
      alert(`Error updating asset: ${error.message}`);
    },
  });

  const handleSaveBillingConfig = () => {
    const typeLower = billingType?.toLowerCase() || "";
    const isPallet = typeLower.includes('pallet');
    const isUnit = billingType === BY_THE_UNIT_ITEM;
    const isSquareFootage = billingType === SQUARE_FOOT_ITEM || billingType === SQUARE_FOOT_LOCATION;
    const isCubicFootage = billingType === CUBIC_FOOT_ITEM || billingType === CUBIC_FOOT_LOCATION;
    const isHundredweight = billingType === HUNDREDWEIGHT;
    
    const input = {
      billingType,
      ...(isPallet && { palletType }),
      ...(isUnit && { unitType }),
      ...(isSquareFootage && {
        squareFootage: parseFloat(squareFootage) || 0,
        dimensions: dimensions.length > 0 ? dimensions : [],
      }),
      ...(isCubicFootage && {
        cubicFootage: parseFloat(cubicFootage) || 0,
        dimensions: dimensions.length > 0 ? dimensions : [],
      }),
      ...(isHundredweight && {
        weight: weight?.toString() || "0",
      }),
    };

    updateAsset({
      variables: {
        id: asset.id,
        input,
      },
    });
  };

  const handleUpdateLocations = () => {
    if (isHistoricalData) {
      setShowReplayLogModal(true);
    } else {
      setShowInventoryModal(true);
    }
  };

  const replayLog = isHistoricalData ? {
    id: assetReplayLogId,
    assetId: asset.id,
    rackLocations: asset.rackLocations,
  } : null;

  const getBillingTypeSubtitle = () => {
    const formula = BILLING_TYPE_FORMULAS[billingType];
    if (!formula) return null;

    // For pallet billing, show live calculation
    const isPallet = billingType === BY_THE_PALLET || billingType === BY_THE_PALLET_WAREHOUSE_BUILT;
    if (isPallet) {
      const numLocations = asset.rackLocations?.length || 0;
      const currentPalletType = palletType || asset.palletType || 'Standard Pallet';
      const billingRates = projectData?.project?.billingRates || {};
      const storageRates = billingRates.storage || {};
      const rateKey = getPalletRateKey(currentPalletType);
      const rate = parseFloat(storageRates[rateKey]) || 0;
      const calculatedTotal = numLocations * rate;

      return {
        formula,
        example: `${currentPalletType}: ${numLocations} ${numLocations === 1 ? 'location' : 'locations'} × $${rate.toFixed(2)} per pallet = $${calculatedTotal.toFixed(2)}`
      };
    }

    // Static examples for other billing types
    const staticExamples = {
      [SQUARE_FOOT_ITEM]: "Example: 100 sq ft × $2.50 per sq ft = $250",
      [SQUARE_FOOT_LOCATION]: "Example: 100 sq ft × $2.50 per sq ft = $250",
      [CUBIC_FOOT_ITEM]: "Example: 50 cu ft × $3.00 per cu ft = $150",
      [CUBIC_FOOT_LOCATION]: "Example: 50 cu ft × $3.00 per cu ft = $150",
      [HUNDREDWEIGHT]: "Example: 500 lbs ÷ 100 × $20 per cwt = $100",
      [STORAGE_LOCATIONS_OCCUPIED]: "Example: 5 locations × $50 per location = $250",
      [BY_THE_UNIT_ITEM]: "Example: 20 units × $5 per unit = $100",
      [BY_THE_VAULT]: "Example: 3 vaults × $100 per vault = $300",
    };

    return { formula, example: staticExamples[billingType] };
  };

  const renderBillingTypeFields = () => {
    const typeLower = billingType?.toLowerCase() || "";
    const isPallet = typeLower.includes('pallet');
    const isUnit = billingType === BY_THE_UNIT_ITEM;
    const isSquareFootage = billingType === SQUARE_FOOT_ITEM || billingType === SQUARE_FOOT_LOCATION;
    const isCubicFootage = billingType === CUBIC_FOOT_ITEM || billingType === CUBIC_FOOT_LOCATION;
    const isHundredweight = billingType === HUNDREDWEIGHT;

    if (isPallet) {
      const palletOptions = getBillingQuantityOptions(billingType, projectData?.project?.palletTypes);
      return (
        <TextField
          select
          label="Pallet Type"
          value={palletType}
          onChange={(e) => setPalletType(e.target.value)}
          fullWidth
          sx={{ mb: 2 }}
        >
          {palletOptions.map(([value, label]) => (
            <MenuItem key={value} value={value}>
              {label}
            </MenuItem>
          ))}
        </TextField>
      );
    }

    if (isUnit) {
      const unitOptions = getBillingQuantityOptions(
        billingType,
        [],
        projectData?.project?.unitTypes
      );
      return (
        <TextField
          select
          label="Unit Type"
          value={unitType}
          onChange={(e) => setUnitType(e.target.value)}
          fullWidth
          sx={{ mb: 2 }}
        >
          {unitOptions.map(([value, label]) => (
            <MenuItem key={value} value={value}>
              {label}
            </MenuItem>
          ))}
        </TextField>
      );
    }

    if (isSquareFootage) {
      const dimensionsDisplay = dimensions?.length > 0
        ? dimensions.map(field => [field.width, field.depth, field.height].join("x")).join(", ")
        : "";
      
      return (
        <>
          <TextField
            label="Square Footage"
            type="number"
            value={squareFootage}
            onChange={(e) => setSquareFootage(e.target.value)}
            fullWidth
            sx={{ mb: 2 }}
            inputProps={{ min: 0, step: 0.01 }}
          />
          <TextField
            label={'Dimensions (W" x D" x H")'}
            value={dimensionsDisplay}
            placeholder="Click to set dimensions"
            fullWidth
            sx={{ mb: 2 }}
            InputProps={{
              readOnly: true,
              sx: { cursor: 'pointer' }
            }}
            onClick={() => setShowDimensionsModal(true)}
          />
        </>
      );
    }

    if (isCubicFootage) {
      const dimensionsDisplay = dimensions?.length > 0
        ? dimensions.map(field => [field.width, field.depth, field.height].join("x")).join(", ")
        : "";
      
      return (
        <>
          <TextField
            label="Cubic Footage"
            type="number"
            value={cubicFootage}
            onChange={(e) => setCubicFootage(e.target.value)}
            fullWidth
            sx={{ mb: 2 }}
            inputProps={{ min: 0, step: 0.01 }}
          />
          <TextField
            label={'Dimensions (W" x D" x H")'}
            value={dimensionsDisplay}
            placeholder="Click to set dimensions"
            fullWidth
            sx={{ mb: 2 }}
            InputProps={{
              readOnly: true,
              sx: { cursor: 'pointer' }
            }}
            onClick={() => setShowDimensionsModal(true)}
          />
        </>
      );
    }

    if (isHundredweight) {
      return (
        <TextField
          label="Weight (lbs)"
          type="number"
          value={weight}
          onChange={(e) => setWeight(e.target.value)}
          fullWidth
          sx={{ mb: 2 }}
          inputProps={{ min: 0, step: 0.01 }}
        />
      );
    }

    return null;
  };

  return (
    <>
      <Box display="flex" flexDirection="column" width="100%" height="100%" position="relative">
        {/* Scrollable Content */}
        <Box flex={1} overflow="auto" pb={2}>
        {/* Combined Billing Inputs Card */}
        <Box 
          mb={3}
          sx={{ 
            p: 2.5, 
            bgcolor: 'grey.50', 
            borderRadius: 1,
            border: '1px solid',
            borderColor: 'grey.200'
          }}
        >
          {/* Billing Type Formula Note */}
          {billingType && (() => {
            const subtitle = getBillingTypeSubtitle();
            if (subtitle) {
              return (
                <Box 
                  sx={{
                    mb: 2.5,
                  }}
                >
                  <Typography 
                    variant="body1" 
                    fontWeight={500}
                    align="center"
                    sx={{ mb: 1.5 }}
                  >
                    {billingType} Calculation
                  </Typography>
                  <Typography variant="body1" color="text.secondary" align="center">
                    {subtitle.formula}
                  </Typography>
                </Box>
              );
            }
            return null;
          })()}

          {/* Billing Type Fields */}
          <Box mb={2}>
            {renderBillingTypeFields()}
          </Box>

          {/* Billing Locations */}
          <TextField
            label="Billing Locations"
            value={
              asset.rackLocations && asset.rackLocations.length > 0
                ? asset.rackLocations
                    .map(rl => formatRackLocationLabel(rl, { includeQty: rl.sub_qty > 0 }))
                    .join('\n')
                : 'No locations set'
            }
            fullWidth
            multiline
            minRows={asset.rackLocations?.length || 1}
            maxRows={15}
            InputProps={{
              readOnly: true,
              sx: { cursor: 'pointer' }
            }}
            onClick={handleUpdateLocations}
            sx={{ 
              '& .MuiOutlinedInput-root': {
                '&:hover': {
                  backgroundColor: 'action.hover',
                },
              },
            }}
          />
        </Box>

        {/* Live Calculation Preview - Single Line */}
        {billingType && (() => {
          const subtitle = getBillingTypeSubtitle();
          if (subtitle) {
            return (
              <Box 
                sx={{ 
                  bgcolor: '#FFFBEB',
                  borderRadius: 1,
                  p: 2,
                }}
              >
                <Typography 
                  variant="body1" 
                  fontWeight={500}
                  align="center"
                  color="text.primary"
                >
                  {subtitle.example}
                </Typography>
              </Box>
            );
          }
          return null;
        })()}
        </Box>

        {/* Footer - Anchored to bottom of modal */}
        <Box
          sx={{
            bgcolor: 'background.paper',
            borderTop: '1px solid',
            borderColor: 'divider',
            p: 2,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginLeft: -5,
            marginRight: -5,
            marginBottom: -3,
            paddingX: 5,
          }}
        >
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={onBack}
        >
          Back
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSaveBillingConfig}
          disabled={updateLoading || !billingType}
          startIcon={updateLoading && <CircularProgress size={20} color="inherit" />}
        >
          {updateLoading ? "Updating..." : "Update"}
        </Button>
      </Box>
      </Box>

      {/* Modals */}
      {showReplayLogModal && replayLog && (
        <CopyReplayLogModal
          open={showReplayLogModal}
          onClose={(newReplayLogId) => {
            setShowReplayLogModal(false);
            
            // Only update URL and refetch if a new replay log was actually created
            if (newReplayLogId && typeof newReplayLogId === 'string' && newReplayLogId !== 'undefined' && onUpdateReplayLogId) {
              onUpdateReplayLogId(newReplayLogId);
              
              // Refetch both asset data and the new replay log data
              apolloClient.refetchQueries({
                include: [ASSET_CHARGES_INFO_QUERY, ASSET_REPLAY_LOG_QUERY],
              });
            }
            // Don't refetch anything if user just closed without saving
          }}
          replayLog={replayLog}
          projectId={asset.project?.id}
        />
      )}

      {showInventoryModal && (
        <InventoryTransactionMultipleModal
          open={showInventoryModal}
          onClose={() => setShowInventoryModal(false)}
          onSuccess={() => {
            setShowInventoryModal(false);
            apolloClient.refetchQueries({
              include: [ASSET_CHARGES_INFO_QUERY],
            });
          }}
          title="Inventory Placement"
          asset={asset}
          displayPalletBreakdown={false}
          displayQuantityCalculation={true}
          displayNewAvailableQuantity={true}
        />
      )}

      {showDimensionsModal && (
        <DimensionsModal
          field={{ label: labels.dimensions }}
          onSubmit={(newDimensions) => {
            setDimensions(newDimensions);
            setShowDimensionsModal(false);
          }}
          value={dimensions}
          nextRow={() => {}}
          onClose={() => setShowDimensionsModal(false)}
        />
      )}
    </>
  );
}
